import React, { useReducer, useRef } from 'react';
import ConversationsContext from './dpxConversationsContext';
import ConversationsReducer from './dpxConversationsReducer';
import api from '../../api/api';
import {
    GET_CONVERSATIONS,
    CLEAR_STATE,
    CLEAR_MESSAGES,
    SEND_MESSAGE,
    CLEAR_ERROR,
    GET_CONVERSATION,
    MESSAGE_SOCKET,
    SET_FINAL,
    CONVERSATION_SOCKET,
    DELETE_CONVERSATION,
    CREATE_LEAD,
    UPDATE_CONVERSATION,
    CREATE_BDC,
    CLEAR_CONVERSATION,
    SET_LOADING,
    SET_CURRENT_CONVERSATION_ID,
} from '../types';
import { HEADERS } from '../../constants/headers';
import { channel } from 'src/service/pusher';
import { useTranslation } from 'react-i18next';
import { AWS_S3 } from 'src/constants/crm/aws';
import axios from 'axios';
import { handleError } from '../utils/handleError';
import { sendWhatsApp } from 'src/shared/communications';
import {
    mapMessagePayloadToSendWhatsAppDTO,
    mapTemplatePayloadToSendWhatsAppDTO,
} from 'src/modules/conversations/helpers/mapMessagePayloadToSendWhatsAppDTO';
import { clearChannelConversationsUnbind } from 'src/modules/conversations/helpers/clearChannelUnbind';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';

const ConversationsState = (props) => {
    const initialState = {
        conversation: {},
        conversations: [],
        chart: [],
        pagination: {},
        lastItems: null,
        messages: [],
        currentBoundConversationId: null,
        loading: false,
        error: null,
    };

    const { t } = useTranslation();

    const [state, dispatch] = useReducer(ConversationsReducer, initialState);
    const currentConversationIdRef = useRef(null);

    const updateConversation = async (values, conversationId) => {
        try {
            const res = await api.put(
                `/dpxConversations/${conversationId}`,
                { ...values },
                HEADERS()
            );
            dispatch({
                type: UPDATE_CONVERSATION,
                payload: res.data.data,
                successMessage: t('SnackBar.ConversationUpdated'),
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const setConversationLead = (lead) => {
        dispatch({
            type: UPDATE_CONVERSATION,
            payload: {
                ...state.conversation,
                lead,
            },
        });
    };

    const createBDC = async (values) => {
        try {
            const res = await api.post(
                '/dpxConversations/bdc',
                { ...values },
                HEADERS()
            );
            dispatch({ type: CREATE_BDC, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const createLead = async (values) => {
        try {
            const res = await api.post(
                '/dpxConversations/leads',
                { ...values },
                HEADERS()
            );

            dispatch({
                type: CREATE_LEAD,
                payload: res.data.data,
                successMessage: t('SnackBar.LeadCreated'),
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const deleteConversation = async (conversationId) => {
        try {
            const res = await api.delete(
                `/dpxConversations/${conversationId}`,
                HEADERS()
            );
            dispatch({ type: DELETE_CONVERSATION, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const sendTemplate = async (values) => {
        await api.post(`/dpxConversations/template`, { ...values }, HEADERS());
    };

    const sendTemplateV2 = async (values) => {
        try {
            const dto = mapTemplatePayloadToSendWhatsAppDTO(values);
            const res = await sendWhatsApp(dto, {
                transport: api,
                endpoint: '/dpxConversations/sendTemplate',
                config: HEADERS(),
            });
            dispatch({ type: SEND_MESSAGE, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getConversations = async (values, page) => {
        try {
            setLoading();
            const res = await api.post(
                `/dpxConversations`,
                { ...values },
                HEADERS()
            );
            dispatch({
                type: GET_CONVERSATIONS,
                payload: res.data.data,
                pagination: res.data.pagination,
                reload: page === 1,
                final: res.data.final,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getConversation = async (conversationId) => {
        try {
            setLoading();
            currentConversationIdRef.current = getDocumentId(conversationId);

            clearChannelConversationsUnbind({
                conversation: state.conversation?._id,
            });

            if (state.currentBoundConversationId)
                await channel.unbind(
                    `conversation_${state.currentBoundConversationId}`
                );

            const res = await api.get(
                `/dpxConversations/${conversationId}?isUpdate=true`,
                HEADERS()
            );
            const { _id: fetchedId } = res.data.data;
            currentConversationIdRef.current = fetchedId;

            await channel.bind(`conversation_${fetchedId}`, (data) => {
                if (String(data.conversation) === String(fetchedId)) {
                    addMessageSocket(data.message, data.conversation);
                }
            });

            dispatch({ type: GET_CONVERSATION, payload: res.data.data });
            dispatch({
                type: SET_CURRENT_CONVERSATION_ID,
                payload: fetchedId,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const addMessageSocket = async (
        message,
        conversation,
        updateConversation = true
    ) => {
        try {
            const messageId = getDocumentId(message);

            if (!messageId) return;

            const res = await api.get(`/messages/${messageId}`, HEADERS());
            const messageData = res.data.data;
            const activeConversationId = currentConversationIdRef.current;

            if (!activeConversationId) return;

            const conversationId =
                getDocumentId(messageData.conversation) ||
                getDocumentId(conversation) ||
                null;

            if (
                !conversationId ||
                String(conversationId) !== String(activeConversationId)
            ) {
                return;
            }

            dispatch({
                type: MESSAGE_SOCKET,
                payload: messageData,
                conversation: conversationId,
                updateConversation,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const addConversationSocket = async (conversation) => {
        let conversationData = conversation;
        if (!conversationData?._id) {
            const res = await api.get(
                `/dpxConversations/${conversationData}?isUpdate=false`,
                HEADERS()
            );
            conversationData = res.data.data;
        }
        dispatch({ type: CONVERSATION_SOCKET, payload: conversationData });
    };

    const sendMessage = async (values, sendType) => {
        try {
            const endpoint = '/dpxConversations/sendMessage';
            const sendOptions = {
                transport: api,
                endpoint,
                config: HEADERS(),
            };

            if (values.files) {
                let files = [...values.files];
                delete values.files;

                for (let i = 0; i < files.length; i++) {
                    let response = await api.post(
                        '/uploads/image',
                        { type: files[i].type, fileName: files[i].name },
                        HEADERS()
                    );
                    await axios.put(response.data.url, files[i], {
                        headers: { 'Content-Type': files[i].type },
                    });

                    const payload = {
                        ...values,
                        MediaUrl0: `${AWS_S3}${response.data.key}`,
                        MediaContentType0: files[i].type.replace(
                            '; codecs=opus',
                            ''
                        ),
                    };
                    const dto = mapMessagePayloadToSendWhatsAppDTO(payload, sendType);
                    const res = await sendWhatsApp(dto, sendOptions);
                    dispatch({ type: SEND_MESSAGE, payload: res.data.data });
                }
            } else if (values.documents) {
                let documents = [...values.documents];
                delete values.documents;

                for (let i = 0; i < documents.length; i++) {
                    const arrayFile = documents[i].file.split('.');
                    const type = arrayFile[arrayFile.length - 1];

                    const payload = {
                        ...values,
                        MediaUrl0: `https://automotive-api.s3.us-east-2.amazonaws.com/${documents[i].file}`,
                        MediaContentType0: type,
                    };
                    const dto = mapMessagePayloadToSendWhatsAppDTO(payload, sendType);
                    const res = await sendWhatsApp(dto, sendOptions);
                    dispatch({ type: SEND_MESSAGE, payload: res.data.data });
                }
            } else {
                const dto = mapMessagePayloadToSendWhatsAppDTO(values, sendType);
                const res = await sendWhatsApp(dto, sendOptions);
                dispatch({ type: SEND_MESSAGE, payload: res.data.data });
            }
            return true;
        } catch (error) {
            dispatch(handleError(error));
            return false;
        }
    };

    const clearError = () => dispatch({ type: CLEAR_ERROR });
    const clearState = () => {
        currentConversationIdRef.current = null;
        dispatch({ type: CLEAR_STATE });
    };
    const clearConversation = () => {
        currentConversationIdRef.current = null;
        dispatch({ type: CLEAR_CONVERSATION });
    };
    const clearMessages = () => dispatch({ type: CLEAR_MESSAGES });
    const setFinal = (value = false) =>
        dispatch({ type: SET_FINAL, payload: value });
    const setLoading = () => dispatch({ type: SET_LOADING });

    return (
        <ConversationsContext.Provider
            value={{
                conversations: state.conversations,
                pagination: state.pagination,
                loading: state.loading,
                error: state.error,
                chart: state.chart,
                conversation: state.conversation,
                lastItems: state.lastItems,
                messages: state.messages,
                final: state.final,
                addConversationSocket,
                clearError,
                getConversations,
                deleteConversation,
                createLead,
                createBDC,
                setConversationLead,
                setFinal,
                updateConversation,
                getConversation,
                clearState,
                addMessageSocket,
                sendMessage,
                clearMessages,
                sendTemplate,
                clearConversation,
                sendTemplateV2,
            }}
        >
            {props.children}
        </ConversationsContext.Provider>
    );
};

export default ConversationsState;
